import { Link } from "react-router";

import { Chip, ChipRow } from "../lib/Chip.tsx";
import { InitialsTile } from "../lib/InitialsTile.tsx";
import { Tag } from "../lib/Tag.tsx";
import { TileRow } from "../lib/Tiles.tsx";
import { WorkThumb } from "../lib/WorkThumb.tsx";
import { AGENT_COPY } from "./agent.constants.ts";
import type { ClientGroup, IdentityCard, MandateRow } from "./agent.model.ts";
import { useRepresentationsController } from "./useRepresentationsController.ts";
import "./agent.css";

/**
 * The works this account represents, gathered under the clients who assigned
 * them.
 *
 * Built out of the same objects the collector's collection screen is: the
 * `TileRow` its dashboard opens with, the `Chip` row its grid filters by, the
 * `Tag` on its cards, the `InitialsTile` in its rail and the `WorkThumb` on
 * every work in the product. That is the whole reason this screen was rebuilt —
 * it had grown a plain `<ul>` of its own with a text link on the end, so an
 * agent's only screen was the one place in ONDO that looked like nothing else
 * in it.
 *
 * Each row links to the work's own record, which is the same screen the
 * collector sees, filtered by the same policies. There is no agent-shaped copy
 * of it: what an agent may do to a work is decided in SQL, and a second screen
 * would be a second opinion about that.
 *
 * There is no portfolio figure here and there never can be. An agent reaches a
 * work and stops — no collection, no sibling works, no placement — so a total
 * would either be wrong or be disclosing something the policies refuse. The
 * counts at the top are all arithmetic over the rows below them.
 *
 * It carries no title of its own: the portal header above it does, and two
 * headings saying "My Representations" one under the other was the first thing
 * the rail made redundant.
 */
export function Representations({
  workBase,
  orgOndoId,
}: {
  /** '/agent/ONDO-5592/work' — where a row links to. */
  workBase: string;
  /** The firm whose workspace this is, or null on a personal one. */
  orgOndoId: string | null;
}) {
  const { view, chooseArtist, toggleClient } = useRepresentationsController(
    workBase,
    orgOndoId,
  );

  if (view.kind === "loading") {
    return (
      <p className="agents__note" role="status">
        {view.message}
      </p>
    );
  }

  if (view.kind === "error") {
    return (
      <p className="agents__note" role="alert">
        {view.message}
      </p>
    );
  }

  if (view.kind === "empty") {
    return (
      <section className="agents">
        {view.identity && <SignedInCard card={view.identity} />}
        <p className="agents__lead">{view.lead}</p>
        <p className="agents__note">{view.detail}</p>
      </section>
    );
  }

  return (
    <section className="agents">
      {view.identity && <SignedInCard card={view.identity} />}

      <TileRow tiles={view.tiles} />

      {/* Absent when there is nothing to choose between — `CollectionToolbar`'s
          rule, applied by the model rather than restated here. */}
      {view.filters.length > 0 && (
        <div className="agents__toolbar">
          <ChipRow label={AGENT_COPY.artistFilter}>
            {view.filters.map((filter) => (
              <Chip
                key={filter.key}
                pressed={filter.key === view.activeFilter}
                onClick={() => chooseArtist(filter.key)}
              >
                {filter.label} <span className="agents__chip-count">{filter.count}</span>
              </Chip>
            ))}
          </ChipRow>
        </div>
      )}

      <p className="agents__count">{view.count}</p>

      {view.noMatches ? (
        <p className="agents__note">{view.noMatches}</p>
      ) : (
        view.groups.map((group) => (
          <ClientPanel
            key={group.key}
            group={group}
            onToggle={() => toggleClient(group.key)}
          />
        ))
      )}
    </section>
  );
}

/**
 * Which account is reading, and from which seat.
 *
 * A `<p>` rather than a heading: it is a statement about the reader, not a
 * section of the document, and the screen already has exactly one heading in the
 * portal bar above it.
 */
function SignedInCard({ card }: { card: IdentityCard }) {
  return (
    <div className="agents__me">
      <InitialsTile initials={card.initials} className="agents__me-initials" />
      <span className="agents__me-body">
        <span className="agents__me-name">
          {card.lead} <strong>{card.name}</strong>
        </span>
        <span className="agents__me-meta">{card.meta}</span>
      </span>
    </div>
  );
}

/**
 * One client, and the works they have put in this agent's hands.
 *
 * The head is a real `<button aria-expanded>` rather than a chevron with an
 * onClick, so folding a client away is reachable by keyboard and announced —
 * the same shape the work record's tabs use.
 */
function ClientPanel({ group, onToggle }: { group: ClientGroup; onToggle: () => void }) {
  return (
    <section className="agents__client">
      <button
        type="button"
        className="agents__client-head"
        aria-expanded={group.open}
        onClick={onToggle}
      >
        <InitialsTile initials={group.initials} className="agents__client-initials" />
        <span className="agents__client-body">
          <span className="agents__client-name">{group.name}</span>
          <span className="agents__client-meta">{group.meta}</span>
        </span>
        <span className="agents__client-chevron" aria-hidden="true">
          {group.open ? "⌄" : "›"}
        </span>
        <span className="agents__client-action">
          {group.open ? AGENT_COPY.collapse : AGENT_COPY.expand}
        </span>
      </button>

      {group.open && (
        <ul className="agents__list">
          {group.rows.map((row) => (
            <MandateRowView key={row.mandateId} row={row} />
          ))}
        </ul>
      )}
    </section>
  );
}

function MandateRowView({ row }: { row: MandateRow }) {
  return (
    <li className="agents__row">
      {/* The image the API has been sending all along. `imagePath` was on
          `AgentMandate` and on the row model, fetched on every load, and the
          view drew a ▤ glyph instead — so an agent's list was the one place in
          ONDO that showed no art. */}
      <span className="agents__thumb">
        <WorkThumb imageUrl={row.imageUrl} title={row.title} />
      </span>

      <span className="agents__body">
        <span className="agents__work">
          {row.title}
          <Tag tone="verified" className="agents__status">
            {row.status}
          </Tag>
        </span>
        <span className="agents__meta">{row.attribution}</span>
        <span className="agents__by">{row.assigned}</span>
      </span>

      <span className="agents__end">
        {row.dimensions && <span className="agents__size">{row.dimensions}</span>}
        <Link className="agents__open" to={row.href}>
          {AGENT_COPY.open} <span aria-hidden="true">→</span>
        </Link>
      </span>
    </li>
  );
}
